import { API_CONFIG, API_ENDPOINTS, authFetch, getHeadersWithOrg } from './client';

/**
 * The publishing contract is the STABLE PUBLIC reference customers use to write
 * their own CSS/JS for published templates (the `tpl-` classes, `--tpl-` CSS
 * variables, and `tpl:` JS events). It lives on the publishing/kg service (the
 * same base URL `getComponents`/`publishFromMap` use), is global (not site- or
 * org-scoped), and is served under the `/flameback/api/v1` prefix.
 *
 * The wire shape is already in the casing the UI consumes (`name`, `description`,
 * `component`, `modifiers`, `default`, `payload`, `version`), so unlike the
 * component catalog there is no snake_case → camelCase remap.
 */

/** A `tpl-` CSS class the published markup exposes for customer styling. */
export interface ContractClass {
  name: string;
  description: string;
  /** The component this class belongs to (used to group the CSS Classes list). */
  component: string;
  /** Modifier classes that can be combined with this base class. */
  modifiers: string[];
}

/** A `--tpl-` CSS custom property customers can override. */
export interface ContractVariable {
  name: string;
  /** The default value the published template applies. */
  default: string;
  description: string;
}

/** A `tpl:` DOM event the published runtime dispatches. */
export interface ContractEvent {
  name: string;
  description: string;
  /** TypeScript-style signature of the event's `detail` payload. */
  payload: string;
}

/** The complete public contract for CSS/JS customization of published templates. */
export interface PublishingContractResponse {
  /** Semver. A major bump means a class/variable was removed or renamed. */
  version: string;
  classes: ContractClass[];
  variables: ContractVariable[];
  events: ContractEvent[];
}

/**
 * Fetch the public CSS/JS customization contract for published templates.
 *
 * The contract is static (it changes only on backend deploys) and the backend
 * sends `Cache-Control: no-store`. Callers cache the result in memory for the
 * lifetime of the page. A non-OK status throws so the caller can surface an error.
 */
export async function getPublishingContract(): Promise<PublishingContractResponse> {
  const response = await authFetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.PUBLISHING_CONTRACT}`,
    { headers: getHeadersWithOrg() },
  );
  if (!response.ok) {
    throw new Error(`Failed to load publishing contract: ${response.status}`);
  }
  return (await response.json()) as PublishingContractResponse;
}
